import type {
  FieldDefinition,
  JsonValue,
  VectorFeature,
  VectorSourceDescriptor,
} from '../data/model';
import type {
  CategorisedClassification,
  CategoryDefinition,
  CategoryValue,
  GraduatedClassDefinition,
  GraduatedClassification,
  GraduatedGeneratedMethod,
  GraduatedVisualVariable,
  NumericClassDefinition,
  ThematicSymbolDefinition,
} from './model';
import {
  categoricalPaletteColours,
  DEFAULT_CATEGORICAL_PALETTE_ID,
  DEFAULT_SEQUENTIAL_PALETTE_ID,
  sequentialPaletteColours,
  type CategoricalPaletteId,
  type SequentialPaletteId,
} from './palettes';

export const MAX_CATEGORISED_VALUES = 24;
export const DEFAULT_GRADUATED_CLASS_COUNT = 5;
export const MIN_GRADUATED_CLASS_COUNT = 3;
export const MAX_GRADUATED_CLASS_COUNT = 7;
export const DEFAULT_POINT_SIZE_RANGE = { minimum: 4, maximum: 12 } as const;
export const DEFAULT_LINE_WIDTH_RANGE = { minimum: 1, maximum: 7 } as const;
export const THEMATIC_NULL_COLOUR = '#cbd5e1';

export type ClassificationGenerationErrorCode =
  'empty-field' | 'high-cardinality' | 'invalid-field';

export interface ClassificationGenerationError {
  readonly code: ClassificationGenerationErrorCode;
  readonly message: string;
}

export type ClassificationGenerationResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: ClassificationGenerationError };

const CATEGORY_KIND_ORDER: Readonly<Record<CategoryValue['kind'], number>> = {
  boolean: 0,
  number: 1,
  string: 2,
  null: 3,
  missing: 4,
};

export function categorisableFields(
  fields: readonly FieldDefinition[],
): readonly FieldDefinition[] {
  return fields.filter((field) => ['boolean', 'integer', 'number', 'string'].includes(field.type));
}

export function numericFields(fields: readonly FieldDefinition[]): readonly FieldDefinition[] {
  return fields.filter((field) => field.type === 'integer' || field.type === 'number');
}

function fieldByName(
  fields: readonly FieldDefinition[],
  fieldName: string,
): FieldDefinition | undefined {
  return fields.find((field) => field.name === fieldName);
}

export function categoryValueKey(value: CategoryValue): string {
  switch (value.kind) {
    case 'boolean':
      return `boolean:${value.value}`;
    case 'number':
      return `number:${Object.is(value.value, -0) ? 0 : value.value}`;
    case 'string':
      return `string:${JSON.stringify(value.value)}`;
    case 'null':
      return 'null';
    case 'missing':
      return 'missing';
  }
}

export function categoryLabel(value: CategoryValue): string {
  switch (value.kind) {
    case 'boolean':
      return value.value ? 'True' : 'False';
    case 'number':
      return String(Object.is(value.value, -0) ? 0 : value.value);
    case 'string':
      return value.value === '' ? '(Empty string)' : value.value;
    case 'null':
      return '(Null)';
    case 'missing':
      return '(Missing)';
  }
}

function compareCategories(left: CategoryValue, right: CategoryValue): number {
  const kindDifference = CATEGORY_KIND_ORDER[left.kind] - CATEGORY_KIND_ORDER[right.kind];
  if (kindDifference !== 0) return kindDifference;
  if (left.kind === 'boolean' && right.kind === 'boolean') {
    return Number(left.value) - Number(right.value);
  }
  if (left.kind === 'number' && right.kind === 'number') return left.value - right.value;
  if (left.kind === 'string' && right.kind === 'string') {
    return left.value < right.value ? -1 : left.value > right.value ? 1 : 0;
  }
  return 0;
}

function categoryValueForFeature(feature: VectorFeature, fieldName: string): CategoryValue | null {
  if (!feature.properties || !Object.hasOwn(feature.properties, fieldName)) {
    return { kind: 'missing' };
  }
  const value = feature.properties[fieldName];
  if (value === null) return { kind: 'null' };
  if (typeof value === 'boolean') return { kind: 'boolean', value };
  if (typeof value === 'number' && Number.isFinite(value)) {
    return { kind: 'number', value: Object.is(value, -0) ? 0 : value };
  }
  if (typeof value === 'string') return { kind: 'string', value };
  return null;
}

export function categoryForFeature(
  feature: VectorFeature,
  fieldName: string,
  categories: readonly CategoryDefinition[],
): CategoryDefinition | undefined {
  const value = categoryValueForFeature(feature, fieldName);
  if (!value) return undefined;
  const key = categoryValueKey(value);
  return categories.find((category) => categoryValueKey(category.value) === key);
}

function colourSymbol(colour: string): ThematicSymbolDefinition {
  return { generated: { colour } };
}

export function createCategorisedClassification(
  source: VectorSourceDescriptor,
  fieldName: string,
  paletteId: CategoricalPaletteId = DEFAULT_CATEGORICAL_PALETTE_ID,
): ClassificationGenerationResult<CategorisedClassification> {
  const field = fieldByName(categorisableFields(source.fields), fieldName);
  if (!field) {
    return {
      ok: false,
      error: {
        code: 'invalid-field',
        message: 'Choose a text, number, or boolean field for Categorised styling.',
      },
    };
  }

  const values = new Map<string, CategoryValue>();
  for (const feature of source.data.features) {
    const value = categoryValueForFeature(feature, field.name);
    if (!value) {
      return {
        ok: false,
        error: {
          code: 'invalid-field',
          message: `“${field.name}” contains arrays or objects and cannot be categorised safely.`,
        },
      };
    }
    values.set(categoryValueKey(value), value);
    if (values.size > MAX_CATEGORISED_VALUES) {
      return {
        ok: false,
        error: {
          code: 'high-cardinality',
          message: `“${field.name}” has more than ${MAX_CATEGORISED_VALUES} distinct values. Choose a field with fewer categories.`,
        },
      };
    }
  }

  if (values.size === 0) {
    return {
      ok: false,
      error: { code: 'empty-field', message: `“${field.name}” has no values to categorise.` },
    };
  }

  const orderedValues = [...values.values()].sort(compareCategories);
  const colours = categoricalPaletteColours(paletteId, orderedValues.length);
  return {
    ok: true,
    value: {
      mode: 'categorised',
      field: field.name,
      paletteId,
      categories: orderedValues.map((value, index) => ({
        value,
        symbol: colourSymbol(colours[index] ?? THEMATIC_NULL_COLOUR),
      })),
      other: colourSymbol(THEMATIC_NULL_COLOUR),
    },
  };
}

function numericValue(value: JsonValue | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function numericValues(
  source: VectorSourceDescriptor,
  fieldName: string,
): readonly number[] {
  return source.data.features.flatMap((feature) => {
    if (!feature.properties || !Object.hasOwn(feature.properties, fieldName)) return [];
    const value = numericValue(feature.properties[fieldName]);
    return value === null ? [] : [Object.is(value, -0) ? 0 : value];
  });
}

export interface ObservedNumericExtent {
  readonly minimum: number;
  readonly maximum: number;
}

export function observedNumericExtent(
  source: VectorSourceDescriptor,
  fieldName: string,
): ObservedNumericExtent | null {
  const values = numericValues(source, fieldName);
  if (values.length === 0) return null;
  let minimum = values[0] as number;
  let maximum = minimum;
  for (const value of values.slice(1)) {
    if (value < minimum) minimum = value;
    if (value > maximum) maximum = value;
  }
  return { minimum, maximum };
}

function midpoint(lower: number, upper: number): number {
  const direct = lower + (upper - lower) / 2;
  if (Number.isFinite(direct) && direct > lower && direct < upper) return direct;
  const split = lower / 2 + upper / 2;
  if (Number.isFinite(split) && split > lower && split < upper) return split;
  return upper;
}

function cleanGeneratedBoundary(value: number): number {
  if (!Number.isFinite(value) || value === 0) return value;
  return Number(value.toPrecision(15));
}

function equalIntervalBoundaries(
  values: readonly number[],
  requestedClassCount: number,
): readonly number[] {
  const distinctValues = [...new Set(values)].sort((left, right) => left - right);
  const minimum = distinctValues[0];
  const maximum = distinctValues.at(-1);
  if (minimum === undefined || maximum === undefined) return [];
  if (minimum === maximum) return [minimum, maximum];
  const count = Math.max(1, Math.min(requestedClassCount, distinctValues.length));
  const interval = (maximum - minimum) / count;
  return Array.from({ length: count + 1 }, (_, index) => {
    if (index === 0) return minimum;
    if (index === count) return maximum;
    return cleanGeneratedBoundary(minimum + interval * index);
  });
}

function quantileBoundaries(
  values: readonly number[],
  requestedClassCount: number,
): readonly number[] {
  const ordered = [...values].sort((left, right) => left - right);
  const distinctCount = new Set(ordered).size;
  const minimum = ordered[0];
  const maximum = ordered.at(-1);
  if (minimum === undefined || maximum === undefined) return [];
  if (minimum === maximum) return [minimum, maximum];

  const count = Math.max(1, Math.min(requestedClassCount, distinctCount));
  const boundaries = [minimum];
  for (let index = 1; index < count; index += 1) {
    let splitIndex = Math.ceil((index * ordered.length) / count);
    splitIndex = Math.max(1, Math.min(ordered.length - 1, splitIndex));
    while (splitIndex < ordered.length && ordered[splitIndex - 1] === ordered[splitIndex]) {
      splitIndex += 1;
    }
    if (splitIndex >= ordered.length) continue;
    const lower = ordered[splitIndex - 1];
    const upper = ordered[splitIndex];
    if (lower === undefined || upper === undefined || lower === upper) continue;
    const boundary = midpoint(lower, upper);
    if (boundary > (boundaries.at(-1) ?? Number.NEGATIVE_INFINITY) && boundary < maximum) {
      boundaries.push(boundary);
    }
  }
  boundaries.push(maximum);
  return boundaries;
}

function generatedSymbolForClass(
  index: number,
  count: number,
  colour: string,
  visualVariable: GraduatedVisualVariable,
): ThematicSymbolDefinition {
  const fraction = count <= 1 ? 0.5 : index / (count - 1);
  if (visualVariable.kind === 'colour') return colourSymbol(colour);
  const value =
    visualVariable.minimum + (visualVariable.maximum - visualVariable.minimum) * fraction;
  return {
    generated: {
      ...(visualVariable.includeColour ? { colour } : {}),
      ...(visualVariable.kind === 'point-size' ? { pointRadius: value } : { lineWidth: value }),
    },
  };
}

function classesFromBoundaries(
  boundaries: readonly number[],
  paletteId: SequentialPaletteId,
  paletteReversed: boolean,
  visualVariable: GraduatedVisualVariable,
): readonly GraduatedClassDefinition[] {
  const classCount = Math.max(1, boundaries.length - 1);
  const colours = sequentialPaletteColours(paletteId, classCount, paletteReversed);
  return Array.from({ length: classCount }, (_, index) => ({
    symbol: generatedSymbolForClass(
      index,
      classCount,
      colours[index] ?? THEMATIC_NULL_COLOUR,
      visualVariable,
    ),
  }));
}

/** Derive contiguous effective ranges from the authoritative shared boundaries. */
export function numericClassesForClassification(
  classification: GraduatedClassification,
): readonly NumericClassDefinition[] {
  if (classification.boundaries.length !== classification.classes.length + 1) return [];
  return classification.classes.flatMap((graduatedClass, index) => {
    const lower = classification.boundaries[index];
    const upper = classification.boundaries[index + 1];
    if (lower === undefined || upper === undefined) return [];
    return [
      {
        lower,
        upper,
        includeUpper: index === classification.classes.length - 1,
        symbol: graduatedClass.symbol,
      },
    ];
  });
}

export function defaultGraduatedVisualVariable(): GraduatedVisualVariable {
  return { kind: 'colour' };
}

export function createGraduatedClassification(
  source: VectorSourceDescriptor,
  fieldName: string,
  classCount = DEFAULT_GRADUATED_CLASS_COUNT,
  paletteId: SequentialPaletteId = DEFAULT_SEQUENTIAL_PALETTE_ID,
  method: GraduatedGeneratedMethod = 'equal-interval',
  visualVariable: GraduatedVisualVariable = defaultGraduatedVisualVariable(),
  paletteReversed = false,
): ClassificationGenerationResult<GraduatedClassification> {
  const field = fieldByName(numericFields(source.fields), fieldName);
  if (!field) {
    return {
      ok: false,
      error: { code: 'invalid-field', message: 'Choose a numeric field for Graduated styling.' },
    };
  }
  if (
    !Number.isInteger(classCount) ||
    classCount < MIN_GRADUATED_CLASS_COUNT ||
    classCount > MAX_GRADUATED_CLASS_COUNT
  ) {
    return {
      ok: false,
      error: {
        code: 'invalid-field',
        message: `Choose between ${MIN_GRADUATED_CLASS_COUNT} and ${MAX_GRADUATED_CLASS_COUNT} classes.`,
      },
    };
  }

  const values = numericValues(source, field.name);
  if (values.length === 0) {
    return {
      ok: false,
      error: { code: 'empty-field', message: `“${field.name}” has no finite numeric values.` },
    };
  }

  const boundaries =
    method === 'quantile'
      ? quantileBoundaries(values, classCount)
      : equalIntervalBoundaries(values, classCount);
  const classes = classesFromBoundaries(boundaries, paletteId, paletteReversed, visualVariable);
  const usesColour = visualVariable.kind === 'colour' || visualVariable.includeColour;
  return {
    ok: true,
    value: {
      mode: 'graduated',
      field: field.name,
      method,
      resetMethod: method,
      classCount,
      paletteId,
      paletteReversed,
      visualVariable,
      boundaries,
      classes,
      nullSymbol: { generated: usesColour ? { colour: THEMATIC_NULL_COLOUR } : {} },
    },
  };
}

export function classForNumericValue(
  classes: readonly NumericClassDefinition[],
  value: unknown,
): NumericClassDefinition | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  return classes.find(
    (numericClass) =>
      value >= numericClass.lower &&
      (numericClass.includeUpper ? value <= numericClass.upper : value < numericClass.upper),
  );
}

export function recolourCategorisedClassification(
  classification: CategorisedClassification,
  paletteId: CategoricalPaletteId,
): CategorisedClassification {
  const colours = categoricalPaletteColours(paletteId, classification.categories.length);
  return {
    ...classification,
    paletteId,
    categories: classification.categories.map((category, index) => ({
      ...category,
      symbol: {
        ...category.symbol,
        generated: {
          ...category.symbol.generated,
          colour: colours[index] ?? THEMATIC_NULL_COLOUR,
        },
      },
    })),
  };
}

export function restyleGraduatedClassification(
  classification: GraduatedClassification,
  paletteId: SequentialPaletteId,
  visualVariable: GraduatedVisualVariable,
): GraduatedClassification {
  const colours = sequentialPaletteColours(
    paletteId,
    classification.classes.length,
    classification.paletteReversed,
  );
  const classes = classification.classes.map((numericClass, index) => ({
    ...numericClass,
    symbol: {
      ...generatedSymbolForClass(
        index,
        classification.classes.length,
        colours[index] ?? THEMATIC_NULL_COLOUR,
        visualVariable,
      ),
      ...(numericClass.symbol.override ? { override: numericClass.symbol.override } : {}),
      ...(numericClass.symbol.legendLabel ? { legendLabel: numericClass.symbol.legendLabel } : {}),
    },
  }));
  const usesColour = visualVariable.kind === 'colour' || visualVariable.includeColour;
  return {
    ...classification,
    paletteId,
    visualVariable,
    classes,
    nullSymbol: {
      ...classification.nullSymbol,
      generated: usesColour ? { colour: THEMATIC_NULL_COLOUR } : {},
    },
  };
}

export function setGraduatedPaletteReversed(
  classification: GraduatedClassification,
  paletteReversed: boolean,
): GraduatedClassification {
  const next = { ...classification, paletteReversed };
  return restyleGraduatedClassification(next, next.paletteId, next.visualVariable);
}

export function recolourGraduatedClassification(
  classification: GraduatedClassification,
  paletteId: SequentialPaletteId,
): GraduatedClassification {
  return restyleGraduatedClassification(classification, paletteId, classification.visualVariable);
}

export function updateGraduatedBoundary(
  classification: GraduatedClassification,
  boundaryIndex: number,
  boundary: number,
): GraduatedClassification | null {
  if (
    !Number.isFinite(boundary) ||
    boundaryIndex <= 0 ||
    boundaryIndex >= classification.boundaries.length - 1
  ) {
    return null;
  }
  const previous = classification.boundaries[boundaryIndex - 1];
  const next = classification.boundaries[boundaryIndex + 1];
  if (previous === undefined || next === undefined || boundary <= previous || boundary >= next) {
    return null;
  }
  return {
    ...classification,
    method: 'manual',
    boundaries: classification.boundaries.map((value, index) =>
      index === boundaryIndex ? boundary : value,
    ),
  };
}
